import React, { useState, useEffect } from 'react';
import { Lightbulb, AlertTriangle, Plus, Trash2, ArrowRight, TrendingDown, Zap } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      onClick={onChange}
      className="relative inline-flex items-center w-10 h-5 rounded-full transition-all duration-200"
      style={{ background: checked ? '#10B981' : 'rgba(100,116,139,0.3)' }}
    >
      <span
        className="inline-block w-4 h-4 rounded-full bg-white transition-transform duration-200"
        style={{ transform: checked ? 'translateX(22px)' : 'translateX(2px)' }}
      />
    </button>
  );
}

export default function Recommendations({ partnerId }) {
  const [products, setProducts] = useState([]);
  const [alternatives, setAlternatives] = useState([]);
  const [budgetBarriers, setBudgetBarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderForm, setBuilderForm] = useState({ premium_product_id: '', alternative_product_id: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [impactStats, setImpactStats] = useState({ shown: 0, purchased: 0 });

  const fetchData = async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1)?.toISOString();

      const [prodRes, altRes, scanRes, altScanRes] = await Promise.all([
        supabase?.from('retail_products')?.select('id, product_name, price, category, product_status')?.eq('partner_id', partnerId)?.eq('product_status', 'active'),
        supabase?.from('product_alternatives')
          ?.select('*, premium:retail_products!product_alternatives_premium_product_id_fkey(product_name, price), alternative:retail_products!product_alternatives_alternative_product_id_fkey(product_name, price)')
          ?.eq('partner_id', partnerId),
        supabase?.from('product_scans')?.select('retail_products(id, product_name, price, category), action_taken')?.eq('partner_id', partnerId),
        supabase?.from('product_scans')?.select('action_taken')?.eq('partner_id', partnerId)?.gte('scanned_at', monthStart),
      ]);

      setProducts(prodRes?.data || []);
      setAlternatives(altRes?.data || []);

      // Budget barriers
      const scans = scanRes?.data || [];
      const productStats = {};
      scans?.forEach(s => {
        const pid = s?.retail_products?.id;
        if (!pid) return;
        if (!productStats?.[pid]) productStats[pid] = { ...s?.retail_products, scans: 0, purchases: 0 };
        productStats[pid].scans++;
        if (s?.action_taken === 'purchased') productStats[pid].purchases++;
      });
      const barriers = Object.values(productStats)
        ?.filter(p => p?.scans >= 3 && (p?.purchases / p?.scans) < 0.2)
        ?.sort((a, b) => b?.scans - a?.scans)
        ?.slice(0, 6);
      setBudgetBarriers(barriers);

      // Impact stats
      const monthScans = altScanRes?.data || [];
      const altShown = monthScans?.length;
      const altPurchased = monthScans?.filter(s => s?.action_taken === 'purchased')?.length;
      setImpactStats({ shown: altShown, purchased: altPurchased });
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [partnerId]);

  const handleCreatePair = async (e) => {
    e?.preventDefault();
    if (builderForm?.premium_product_id === builderForm?.alternative_product_id) {
      setMsg('Premium and alternative products must be different');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase?.from('product_alternatives')?.insert({
        ...builderForm,
        partner_id: partnerId,
        is_active: true,
      });
      if (error) { setMsg(error?.message); return; }
      setMsg('');
      setShowBuilder(false);
      setBuilderForm({ premium_product_id: '', alternative_product_id: '' });
      fetchData();
    } catch (e) { setMsg('Failed to create pair'); } finally { setSaving(false); }
  };

  const handleToggle = async (alt) => {
    await supabase?.from('product_alternatives')?.update({ is_active: !alt?.is_active })?.eq('id', alt?.id);
    setAlternatives(a => a?.map(x => x?.id === alt?.id ? { ...x, is_active: !x?.is_active } : x));
  };

  const handleDelete = async (id) => {
    if (!confirm('Remove this product pair?')) return;
    await supabase?.from('product_alternatives')?.delete()?.eq('id', id);
    setAlternatives(a => a?.filter(x => x?.id !== id));
  };

  const selectStyle = { background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' };
  const activeAlts = alternatives?.filter(a => a?.is_active)?.length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Recommendations Engine</h2>
        <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Help budget-conscious consumers find alternatives</p>
      </div>

      {/* Impact Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-2xl p-4" style={{ background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.12)' }}>
          <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Active Pairs</p>
          <p className="text-2xl font-bold mt-1" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>{activeAlts}</p>
        </div>
        <div className="rounded-2xl p-4" style={{ background: 'rgba(123,47,190,0.05)', border: '1px solid rgba(123,47,190,0.12)' }}>
          <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Suggestions Shown (Month)</p>
          <p className="text-2xl font-bold mt-1" style={{ color: '#7B2FBE', fontFamily: 'DM Sans, sans-serif' }}>{impactStats?.shown}</p>
        </div>
        <div className="rounded-2xl p-4" style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.12)' }}>
          <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Led to Purchase (Month)</p>
          <p className="text-2xl font-bold mt-1" style={{ color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>{impactStats?.purchased}</p>
        </div>
      </div>

      {/* Budget Barriers */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(245,158,11,0.15)' }}>
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle size={16} style={{ color: '#F59E0B' }} />
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Budget Barrier Products</h3>
          <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B', fontFamily: 'DM Sans, sans-serif' }}>High interest, low conversion</span>
        </div>
        {loading ? (
          <div className="space-y-2">{[...Array(3)]?.map((_, i) => <div key={i} className="h-14 rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.03)' }} />)}</div>
        ) : budgetBarriers?.length === 0 ? (
          <div className="flex flex-col items-center py-8 gap-2">
            <TrendingDown size={24} style={{ color: '#1E3A5F' }} />
            <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No budget barriers detected yet</p>
            <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>Products need 3+ scans with &lt;20% conversion to appear here</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {budgetBarriers?.map((p, i) => (
              <div key={p?.id || i} className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.1)' }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(245,158,11,0.15)' }}>
                  <AlertTriangle size={15} style={{ color: '#F59E0B' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-white truncate" style={{ fontFamily: 'DM Sans, sans-serif' }}>{p?.product_name}</p>
                    <span className="text-xs px-1.5 py-0.5 rounded-md flex-shrink-0" style={{ background: 'rgba(245,158,11,0.2)', color: '#F59E0B', fontFamily: 'DM Sans, sans-serif' }}>Budget Barrier</span>
                  </div>
                  <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{p?.category} · ${parseFloat(p?.price || 0)?.toFixed(2)}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-bold" style={{ color: '#F59E0B', fontFamily: 'DM Sans, sans-serif' }}>{p?.scans} scans</p>
                  <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{p?.scans > 0 ? Math.round((p?.purchases / p?.scans) * 100) : 0}% conv.</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alternative Product Pairs */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.08)' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lightbulb size={16} style={{ color: '#00E5FF' }} />
            <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Alternative Product Pairs</h3>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,229,255,0.08)', color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>{alternatives?.length} pairs</span>
          </div>
          <button
            onClick={() => setShowBuilder(s => !s)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium"
            style={{ background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.15)', color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}
          >
            <Plus size={13} /> Add Pair
          </button>
        </div>

        {showBuilder && (
          <form onSubmit={handleCreatePair} className="mb-4 p-4 rounded-xl space-y-3" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(0,229,255,0.1)' }}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Premium Product (higher price)</label>
                <select value={builderForm?.premium_product_id} onChange={e => setBuilderForm(f => ({ ...f, premium_product_id: e?.target?.value }))} required
                  className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none" style={selectStyle}>
                  <option value="" style={{ background: '#0D1B2A' }}>Select premium product</option>
                  {products?.map(p => <option key={p?.id} value={p?.id} style={{ background: '#0D1B2A' }}>{p?.product_name} (${parseFloat(p?.price || 0)?.toFixed(0)})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Budget Alternative (lower price)</label>
                <select value={builderForm?.alternative_product_id} onChange={e => setBuilderForm(f => ({ ...f, alternative_product_id: e?.target?.value }))} required
                  className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none" style={selectStyle}>
                  <option value="" style={{ background: '#0D1B2A' }}>Select alternative</option>
                  {products?.map(p => <option key={p?.id} value={p?.id} style={{ background: '#0D1B2A' }}>{p?.product_name} (${parseFloat(p?.price || 0)?.toFixed(0)})</option>)}
                </select>
              </div>
            </div>
            {msg && <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{msg}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={() => { setShowBuilder(false); setMsg(''); }} className="px-4 py-2 rounded-xl text-xs" style={{ background: 'rgba(255,255,255,0.05)', color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Cancel</button>
              <button type="submit" disabled={saving} className="px-4 py-2 rounded-xl text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}>
                {saving ? 'Creating...' : 'Create Pair'}
              </button>
            </div>
          </form>
        )}

        {alternatives?.length === 0 ? (
          <div className="flex flex-col items-center py-8 gap-2">
            <Zap size={24} style={{ color: '#1E3A5F' }} />
            <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No product pairs yet</p>
            <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>Create pairs to suggest budget alternatives to consumers</p>
          </div>
        ) : (
          <div className="space-y-2">
            {alternatives?.map(alt => (
              <div key={alt?.id} className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.04)' }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{alt?.premium?.product_name}</span>
                    <ArrowRight size={13} style={{ color: '#64748B', flexShrink: 0 }} />
                    <span className="text-sm" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>{alt?.alternative?.product_name}</span>
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>
                    ${parseFloat(alt?.premium?.price || 0)?.toFixed(0)} → ${parseFloat(alt?.alternative?.price || 0)?.toFixed(0)}
                    {alt?.premium?.price && alt?.alternative?.price && parseFloat(alt?.premium?.price) > parseFloat(alt?.alternative?.price) && (
                      <span style={{ color: '#10B981' }}> · save ${(parseFloat(alt?.premium?.price) - parseFloat(alt?.alternative?.price))?.toFixed(0)}</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <ToggleSwitch checked={alt?.is_active} onChange={() => handleToggle(alt)} />
                  <button onClick={() => handleDelete(alt?.id)} style={{ color: '#EF4444' }}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-3 rounded-xl" style={{ background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.08)' }}>
        <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>
          <span style={{ color: '#00E5FF' }}>Active pairs</span> are shown to consumers in the AR scanner when they scan the premium product, helping budget-conscious shoppers find alternatives.
        </p>
      </div>
    </div>
  );
}
